import { useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { Metadata } from '@mint-sdk/api-mdm';

import type {
  PlatformByLabel,
  UseMdmPlatformsOptions,
  UseMdmPlatformsResult,
} from './useMdmPlatforms.types.js';
import { useOmniPlatformStrategy } from './useOmniPlatformStrategy.js';
import { useService } from './useService.js';

/**
 * Load MDM platform metadata and provide convenient accessors.
 *
 * Fetches the integration context metadata for the `platform_v2` field and returns:
 * - `platforms`: the array of platform options (or a filtered OMNI list when `onlyOmni` is true)
 * - `platformByLabel`: a lookup object keyed by platform `value`
 * - `isLoading`: query loading state
 *
 * @param {Object} [options] - Hook options.
 * @param {number} [options.companyId] - Company id used to fetch context metadata. When omitted the query is disabled.
 * @param {boolean} [options.onlyOmni=false] - If true, return only platforms listed in `OMNI_MDM_PLATFORMS`.
 * @returns {{ platforms: Metadata.MdmPlatformFieldValueType[]; platformByLabel: Record<string, Metadata.MdmPlatformFieldValueType>; isLoading: boolean }}
 *   An object containing the platforms array, a lookup by platform label, and the loading flag.
 */
export const useMdmPlatformsOld = (
  options: UseMdmPlatformsOptions,
): UseMdmPlatformsResult => {
  const { companyId, onlyOmni, context = 'integration' } = options ?? {};
  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });
  const omniStrategy = useOmniPlatformStrategy();

  const { data: contextData, isLoading } = useQuery(
    {
      ...Metadata.getContextMetadata({
        params: {
          fieldUrnIds: ['platform_v2'],
          companyId,
          context,
        },
      }),
      enabled: typeof companyId === 'number',
    },
    queryClient,
  );

  const platforms = useMemo(() => {
    const platformContextOptions =
      contextData?.[
        Object.keys(contextData).find((key) => key.includes('platform_v2')) ??
          ''
      ]?.options ?? [];

    if (onlyOmni) {
      return omniStrategy
        .getPlatforms(platformContextOptions)
        .filter((platform) => platform !== null);
    }

    return platformContextOptions;
  }, [contextData, onlyOmni, omniStrategy]);

  const platformByLabel = useMemo(
    () =>
      (platforms ?? []).reduce(
        (acc: PlatformByLabel, item) => ({
          ...acc,
          ...(item ? { [item.value]: item } : null),
        }),
        {},
      ),
    [platforms],
  );

  return { platforms, platformByLabel, isLoading };
};
